// AI Context — Context Rail module. Two tabs, honest about what's real vs engine-gated:
//  • Context = the AI SUMMARY, STAGED — generation is the PERLab forward-half engine (CFR-VW-026,
//    not built). We never fake the LLM; instead we show the grounding IS assembled (N cited sources).
//  • Sources = REAL grounding for the current scope: every CITED chunk across the DF-4 context graphs
//    visible here, rendered with the citation primitives (Citation → CitationPopover → SourceArtifactPanel).
// All reads go through useScopeData so the ethical wall holds (DF-4 graphs + DF-2 sources + the
// wall-enforced /context-graphs/{id}/chunks endpoint).
import { type ReactElement, useCallback, useEffect, useState } from "react";
import { Quote } from "lucide-react";
import { RightInspectorSection, Tabs, useInspectorFilter } from "@perlab/ui";

import { useScopeData } from "../state/useScopeData";
import { Citation, CitationPopover, SourceArtifactPanel, buildCitationId } from "../citation";
import type { CitationRef, ProvenanceChunk, SourceLite } from "../citation";
import {
  CitationRevocationChip,
  useRevocationViewMap,
  useCitationRevocationActions,
} from "../citation-chip";
import { useProfile } from "../api/useProfile";

const REVOCATION_API = { apiBase: "/api/workspace" } as const;

interface ContextGraphLite {
  id: string;
  retrieved_chunk_ids: string[];
  cited_chunk_ids: string[];
}

type GView =
  | { kind: "idle" }
  | { kind: "loading" }
  | { kind: "error"; message: string }
  | { kind: "ready"; citations: CitationRef[] };

// Assemble the scope's grounding: the CITED chunks across every visible context graph, deduped,
// numbered, joined to their source artifacts. This is exactly what an AI answer WOULD cite.
function useGrounding(): GView {
  const { ready, fetchScoped } = useScopeData();
  const [view, setView] = useState<GView>({ kind: "idle" });

  const load = useCallback(async () => {
    if (!ready) {
      setView({ kind: "idle" });
      return;
    }
    setView({ kind: "loading" });
    try {
      // D-10: both list endpoints return the canonical Page envelope — read .items.
      const [graphsPage, sourcesPage] = await Promise.all([
        fetchScoped<{ items: ContextGraphLite[] }>("/context-graphs"),
        fetchScoped<{ items: SourceLite[] }>("/sources"),
      ]);
      const graphs = graphsPage.items;
      const sourceById = new Map(sourcesPage.items.map((s) => [s.id, s]));
      // Fetch every graph's chunks in parallel (post-build critique: a sequential per-graph
      // await turns this into a waterfall that scales linearly with graph count) — order of
      // the resolved array is preserved so the dedup pass below stays deterministic.
      const perGraphChunks = await Promise.all(
        graphs.map((g) =>
          fetchScoped<ProvenanceChunk[]>(`/context-graphs/${g.id}/chunks`).then((chunks) => ({
            graphId: g.id,
            chunks,
          })),
        ),
      );
      const seen = new Set<string>();
      // Row 19.5 — citationId needs the owning context-graph id, so track it alongside the
      // deduped chunk (the chunk alone doesn't carry it). A chunk cited via more than one
      // visible graph keeps only the first graph encountered (graphs[] order) — citationId is
      // display/dedup-scoped here, not a stable cross-graph identity.
      const cited: Array<{ chunk: ProvenanceChunk; graphId: string }> = [];
      for (const { graphId, chunks } of perGraphChunks) {
        for (const c of chunks) {
          if (c.role === "cited" && !seen.has(c.chunk_id)) {
            seen.add(c.chunk_id);
            cited.push({ chunk: c, graphId });
          }
        }
      }
      const citations: CitationRef[] = cited.map(({ chunk, graphId }, i) => ({
        index: i + 1,
        chunk,
        source: sourceById.get(chunk.source_id),
        citationId: buildCitationId(graphId, chunk.chunk_id),
        sourceType: "source_artifact",
        sourceRef: { sourceId: chunk.source_id, chunkId: chunk.chunk_id },
      }));
      setView({ kind: "ready", citations });
    } catch (e) {
      setView({ kind: "error", message: e instanceof Error ? e.message : String(e) });
    }
  }, [ready, fetchScoped]);

  useEffect(() => {
    void load();
  }, [load]);

  return view;
}

function ContextTab({ groundingCount }: { groundingCount: number | null }): ReactElement {
  // AI block — visually distinct: 1px DASHED purple border + purple-a06 fill (spec). Honest: the summary is
  // engine-gated (CFR-VW-026); what's "ready" is the grounding, not a faked LLM answer.
  return (
    <div className="px-3 py-2.5">
      <div className="rounded-[2px] border border-dashed border-[var(--color-extended-purple-50)] bg-[var(--color-extended-purple-50)]/[0.06] p-2.5 text-xs text-[var(--color-text-secondary)]">
        <span className="mb-1.5 inline-flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-[0.09em] text-[var(--color-extended-purple-60)]">
          <Quote size={13} aria-hidden /> Grounding-ready
        </span>
        <p>
          Generation arrives with the PERLab forward-half engine (CFR-VW-026). The grounding it will cite is
          assembled in the Sources tab — engine-grounded, never a naked model.
        </p>
        {groundingCount !== null && (
          <p className="mt-1.5 text-[var(--color-text-primary)]">
            Grounding ready: {groundingCount} cited source{groundingCount === 1 ? "" : "s"} at this scope.
          </p>
        )}
      </div>
    </div>
  );
}

function SourcesTab({ view }: { view: GView }): ReactElement {
  const [openIdx, setOpenIdx] = useState<number | null>(null);
  const [source, setSource] = useState<SourceLite | null>(null);
  // Panel filter (audit-03 IR-H1): narrow the cited sources in place by filename / source id. Hook called
  // unconditionally (empty list until ready) so order is stable across renders.
  const citations = view.kind === "ready" ? view.citations : [];
  const { filtered, hasQuery, query } = useInspectorFilter(
    citations,
    (c) => `${c.source?.filename ?? ""} ${c.chunk.source_id}`,
  );

  // C2 revocation surface (REVOCATION-CHIP-01): the citation-chip module resolves, per source
  // artifact, whether it has been REVOKED and (if so) the tombstone/ack state to show. Only revoked
  // artifacts yield a CitationView; a live source contributes nothing (empty map → no chip). Hooks are
  // called unconditionally here (before the early returns below) so hook order is stable across renders.
  const profile = useProfile();
  const sourceIds = citations.map((c) => c.chunk.source_id);
  const revocationByArtifact = useRevocationViewMap(sourceIds, {
    ...REVOCATION_API,
    enabled: view.kind === "ready",
  });
  const actions = useCitationRevocationActions({
    ...REVOCATION_API,
    getActor: () => profile.data?.subject_id ?? null,
  });

  if (view.kind === "idle") {
    return <p className="px-3 py-1.5 text-xs text-[var(--color-text-secondary)]">No scope resolved yet.</p>;
  }
  if (view.kind === "loading") {
    return <p role="status" className="px-3 py-1.5 text-xs text-[var(--color-text-secondary)]">Assembling grounding…</p>;
  }
  if (view.kind === "error") {
    return (
      <p role="alert" className="px-3 py-1.5 text-xs text-[var(--color-text-secondary)]">
        Couldn’t load grounding: {view.message}
      </p>
    );
  }
  if (view.citations.length === 0) {
    return (
      <p className="px-3 py-1.5 text-xs text-[var(--color-text-secondary)]">
        No cited sources ground AI at this scope yet.
      </p>
    );
  }
  return (
    <div>
      <p className="px-3 py-1.5 text-xs text-[var(--color-text-secondary)]">
        {view.citations.length} cited source{view.citations.length === 1 ? "" : "s"} ground AI at this scope.
        {hasQuery && ` ${filtered.length} match “${query}”.`}
      </p>
      <ul className="flex flex-col">
        {filtered.map((c) => {
          const open = openIdx === c.index;
          return (
            <li key={c.chunk.chunk_id} className="flex flex-col px-3 py-1">
              <div className="flex items-center gap-2">
                <Citation
                  index={c.index}
                  role={c.chunk.role}
                  active={open}
                  onActivate={() => setOpenIdx(open ? null : c.index)}
                />
                <span
                  className="min-w-0 flex-1 truncate text-xs text-[var(--color-text-secondary)]"
                  title={c.source?.filename ?? c.chunk.source_id}
                >
                  {c.source?.filename ?? `source ${c.chunk.source_id.slice(0, 8)}…`}
                </span>
              </div>
              {open && (
                <div className="ml-6 mt-1 rounded-[2px] border border-[var(--color-border-subtle)] bg-[var(--color-surface-base)]">
                  <CitationPopover citation={c} onViewSource={() => setSource(c.source ?? null)} />
                  {(() => {
                    // If this citation's SOURCE ARTIFACT has been revoked, surface the tombstone chip
                    // with its acknowledge/escalate actions. A live (non-revoked) source has no entry
                    // in the map → renders nothing (the chip itself also renders null for kind=none).
                    const rv = revocationByArtifact[c.chunk.source_id];
                    if (!rv) return null;
                    return (
                      <div className="border-t border-[var(--color-border-subtle)] px-2 py-1.5">
                        <CitationRevocationChip
                          citation={rv}
                          isPending={actions.isPendingFor(rv.citation_id)}
                          error={actions.errorFor(rv.citation_id)}
                          optimisticAction={actions.optimisticActionFor(rv.citation_id)}
                          onAction={actions.onAction}
                          onDismissError={actions.clearError}
                        />
                      </div>
                    );
                  })()}
                </div>
              )}
            </li>
          );
        })}
      </ul>
      {source && (
        <div className="px-3 pt-1">
          <SourceArtifactPanel source={source} />
        </div>
      )}
    </div>
  );
}

export function AiContextModule(): ReactElement {
  const view = useGrounding();
  const groundingCount = view.kind === "ready" ? view.citations.length : null;
  return (
    <RightInspectorSection id="ai-context" title="AI Context" icon={<Quote size={14} aria-hidden />}>
      <Tabs
        items={[
          { id: "context", label: "Summary", content: <ContextTab groundingCount={groundingCount} /> },
          { id: "sources", label: "Sources", content: <SourcesTab view={view} /> },
        ]}
        defaultActiveId="context"
      />
    </RightInspectorSection>
  );
}
